import type {
  DataGridColumnSettings,
  DropDownResult,
  ModalDialogButtonSetting,
  OdinDataRetrievalOptions,
} from '@myosh/odin-components';
import { CheckboxGroup, Dialog } from '@myosh/odin-components';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { FieldSorts } from '../../../../@types/common';
import { useAppDispatch, useAppSelector } from '../../../../redux/hooks';
import { submitExportFullRecords } from '../../../../redux/slices/background-job-slice';
import { selectedGlobalHierarchies, selectedMatchingOption } from '../../../../redux/slices/global-hierarchies';
import { buildGlobalHierarchyFiltersUrl } from '../../../../utils/filter-url-builder-utils';
import { buildSortedFieldsUrlString } from '../../../../utils/sort-url-builder-utils';

interface ExportFullRecordsModalProps {
  visible: boolean;
  onClose: () => void;
  selectedForm?: DropDownResult;
  gridOptions?: OdinDataRetrievalOptions;
  columns?: DataGridColumnSettings[];
  sortDirective: FieldSorts;
}

export interface InitialExportWizardStateProps {
  includeLinkedRecords: boolean;
  includeAttachments: boolean;
  format: string;
}

export const initialExportWizardState: InitialExportWizardStateProps = {
  includeLinkedRecords: false,
  includeAttachments: false,
  format: 'PDF',
};

const ExportFullRecordDataModal = ({
  columns,
  selectedForm,
  gridOptions,
  visible,
  onClose,
  sortDirective,
}: ExportFullRecordsModalProps) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const globalFilters = useAppSelector(selectedGlobalHierarchies);
  const globalFiltersMatchingOption = useAppSelector(selectedMatchingOption);

  const [exportWizardState, setExportWizardState] = useState<InitialExportWizardStateProps>(initialExportWizardState);

  const settingsOptions = [
    { text: t('include-linked-records'), value: 'linkedRecords' },
    { text: t('include-attachments'), value: 'attachments' },
  ];

  const handleFullRecordDataExport = () => {
    dispatch(
      submitExportFullRecords({
        exportFullRecordData: true,
        formId: selectedForm?.value as string,
        formName: selectedForm?.text as string,
        gridOptions: {
          fieldFilters: gridOptions?.fieldFilters,
        },
        globalHierarchyFilters: buildGlobalHierarchyFiltersUrl(globalFilters, globalFiltersMatchingOption),
        sortDirective: buildSortedFieldsUrlString(sortDirective, columns),
        ...exportWizardState, // format is 'PDF'
      })
    );
  };

  const onModalClose = () => {
    onClose();
    setExportWizardState(initialExportWizardState);
  };

  const buttons: ModalDialogButtonSetting[] = [
    { text: t('cancel'), name: 'cancel', onClick: onModalClose, closeModalOnClick: true },
    {
      text: t('export'),
      name: 'Export',
      type: 'primary',
      onClick: handleFullRecordDataExport,
      closeModalOnClick: true,
    },
  ];

  return (
    <>
      {visible && (
        <Dialog
          header={t('choose-settings')}
          visible={visible}
          onClose={onModalClose}
          containerClassName="max-w-96"
          buttons={buttons}
        >
          <p className="pb-2 pt-3 font-normal">{t('export-full-records-data-first-paragraph')}</p>
          <p className="pb-2 pt-3 font-normal">{t('export-full-records-data-second-paragraph')}</p>
          <p className="pb-2 pt-3 font-normal">{t('export-full-records-data-third-paragraph')}</p>
          <CheckboxGroup
            name="attachments"
            id="attachments"
            textField="text"
            valueField="value"
            className="pt-4 font-medium"
            data={settingsOptions}
            onChange={(value) =>
              setExportWizardState((prevState: InitialExportWizardStateProps) => ({
                ...prevState,
                includeAttachments: value?.some((item) => item?.value === 'attachments') ?? false,
                includeLinkedRecords: value?.some((item) => item?.value === 'linkedRecords') ?? false,
              }))
            }
          />
        </Dialog>
      )}
    </>
  );
};

export default ExportFullRecordDataModal;
